/**
 * Runtime contract for the shared previewable paid boundary (gap G5).
 *
 * It lives in its own module, beside the adapter but out of the component file,
 * for two reasons: every archetype that adopts `FmUnlockPreview` inherits the
 * same guarantees rather than re-deriving them, and an exported function in the
 * component file makes react-docgen-typescript attribute its doc block to the
 * COMPONENT — which is how FmUnlockPreview's catalog entry lost its own props.
 * FmOperationalWorkspaceRoot adopts this in
 * 6wbfb.6.5.
 *
 * SCOPE: this checks STRUCTURE — that every owner-facing string is present and
 * non-empty, so a boundary can never render with a blank price. It deliberately
 * does NOT police vocabulary. The OPJ-21 rule that the owner never sees credits,
 * quota or remaining counts is enforced statically by
 * `tests/architecture/paid-boundary-vocabulary.test.ts`, where a false positive
 * is a red test. Enforcing it here would mean a word list deciding, at render
 * time, whether to throw — and one wrong match (a served "credit card" price,
 * the Finnish "kiinteistö") would blank the page instead of failing a build.
 */
export const FM_UNLOCK_PREVIEW_COPY_KEYS = [
  'title',
  'summary',
  'priceLabel',
  'previewNote',
  'unlockLabel',
] as const;

export function assertFmUnlockPreviewDescriptor(
  value: unknown,
  path: string,
  fail: (at: string, reason: string) => never,
): void {
  if (typeof value !== 'object' || value === null) fail(path, 'must be an object');
  const preview = value as Record<string, unknown>;
  for (const key of FM_UNLOCK_PREVIEW_COPY_KEYS) {
    const candidate = preview[key];
    /* The price is SERVED. An empty string would render a card with a blank
       price and let a caller ship a boundary with no stated cost. */
    if (typeof candidate !== 'string' || candidate.trim().length === 0) {
      fail(`${path}.${key}`, 'must be non-empty served copy');
    }
  }
  if (typeof preview.unlockActionId !== 'string' || preview.unlockActionId.trim().length === 0) {
    fail(`${path}.unlockActionId`, 'must name a declared action');
  }
}
